import { useFocusEffect, useNavigation, useRoute } from '@react-navigation/native';
import { useCallback, useRef, useState } from 'react';
import { HeaderConfig } from '@v2/pages/common/components/ContextHeader/ContextHeader';
import { PaginatedResponse } from '@interfaces/machines';
import { NCFileSearchItem, OperationSearchItem, PartSearchItem } from '@interfaces/search';
import {
  selectForceUpdateHeaderConfig,
  setJobId,
  setJobs,
  setNcFileData,
  setParts,
  setPartId,
  setNcFileId,
  getRecentlyAddedLinks,
} from '../../../../../reducers/details';
import { useSelector } from 'react-redux';
import { GetBatchesByEntityId, GetNCFileById, LoadSingleBatch, SearchEntity } from '@interfaces/methods';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { parseRouteParamAsNumber } from '@helpers';
import { setBatchId } from '@reducers/batch';

interface UseNCFileDetailsProps {
  ncFileId?: number;
  getProductionBatchByNCFile: GetBatchesByEntityId;
  getNCFileById: GetNCFileById;
  getTools?: (param: { ncFileId: number }) => Promise<any[]>;
  clearTools?: () => void;
  updateHeaderConfig?: (headerConfig: HeaderConfig) => void;
  getPartByID?: (partId: number) => Promise<PaginatedResponse<PartSearchItem>>;
  operationsSearch?: SearchEntity<OperationSearchItem>;
  loadAllJobs?: boolean;
  getNCFileByName?: (name: string, select?: string[]) => Promise<PaginatedResponse<NCFileSearchItem>>;
  loadSingleBatch?: LoadSingleBatch;
}

export const useNCFileDetails = ({
  ncFileId: _ncFileId,
  getProductionBatchByNCFile,
  getNCFileById,
  getTools,
  clearTools,
  updateHeaderConfig,
  getPartByID,
  operationsSearch,
  loadAllJobs,
  getNCFileByName,
  loadSingleBatch,
}: UseNCFileDetailsProps) => {
  const route = useRoute();
  const navigation = useNavigation<any>();
  const dispatch = useAppDispatch();

  const { recentlyLinkedJobIdsSet } = dispatch(getRecentlyAddedLinks());

  const [ncFileDataLoading, setNcFileDataLoading] = useState<boolean>(false);
  const [partDataLoading, setPartDataLoading] = useState<boolean>(true);
  const [jobDataLoading, setJobDataLoading] = useState<boolean>(true);

  const forceUpdateHeaderConfig = useSelector(selectForceUpdateHeaderConfig);

  const [ncFileIdState, setNcFileIdState] = useState<number | null>(null);

  const ncFileIdRef = useRef<number | null>(null);

  const ncFileId = _ncFileId ?? parseRouteParamAsNumber(route.params?.ncFileId);
  const ncFileName = route?.params?.ncFileName;

  const loadAndSetJobs = async (_ncFileId?: number) => {
    if ((!ncFileId == null && _ncFileId == null) || !operationsSearch) {
      setJobDataLoading(false);
      return;
    }

    const existingNcFileId = ncFileId ?? _ncFileId;

    const recentlyLinkedJobIdsFilter =
      recentlyLinkedJobIdsSet.size > 0 ? [...recentlyLinkedJobIdsSet].map((id) => `ProductionOperationId eq ${id}`).join(' or ') : '';

    const ncFileFilter = `LinkedNcFiles/any(link: link/NcFileId eq ${existingNcFileId})`;
    const combinedFilter = recentlyLinkedJobIdsFilter ? `(${ncFileFilter}) or (${recentlyLinkedJobIdsFilter})` : ncFileFilter;

    const jobsData = await operationsSearch({
      data: { ...(!loadAllJobs ? { top: 1 } : {}) },
      filter: { oDataFilter: combinedFilter, orderBy: ['ModifiedOn desc'] },
      select: null,
      resetHistory: true,
      ignoreHistory: true,
    });

    if (jobsData.searchResult) {
      dispatch(setJobs(jobsData.searchResult));
      if (jobsData.searchResult?.length === 1) {
        dispatch(setJobId(jobsData.searchResult?.[0]?.productionOperationId));
      }
    }
    setJobDataLoading(false);
    return jobsData;
  };

  const loadAndSetParts = async (partId: number | undefined | null) => {
    if (!getPartByID || !partId) {
      setPartDataLoading(false);
      dispatch(setParts([]));
      if (partId != null) {
        dispatch(setPartId(partId));
      }
      return;
    }
    const partDetails = await getPartByID(partId);
    if (partDetails.searchResult.length) {
      dispatch(setParts([partDetails.searchResult[0]]));
      dispatch(setPartId(partDetails.searchResult?.[0]?.partId));
    }
    setPartDataLoading(false);
    return partDetails;
  };

  const loadBatches = async (_ncFileId?: number) => {
    if (!ncFileId && _ncFileId) return;
    const batches = await getProductionBatchByNCFile({
      id: ncFileId ?? _ncFileId,
      filters: { top: 1 },
      select: ['BatchNumber', 'ProductionBatchId'],
    });
    const loadedBatchId = batches.searchResult[0]?.productionBatchId;
    if (batches?.totalResultCount === 1) {
      if (loadedBatchId == null) return;
      dispatch(setBatchId(loadedBatchId));
      await loadSingleBatch?.(loadedBatchId);
    }
    return batches;
  };

  const loadAndSetNCFile = async () => {
    if (!ncFileId) {
      if (!ncFileName || !getNCFileByName) return;

      setNcFileDataLoading(true);
      const ncFileData = await getNCFileByName(ncFileName);

      if (ncFileData) {
        dispatch(setNcFileData(ncFileData.searchResult?.[0]));
        dispatch(setNcFileId(ncFileData.searchResult?.[0]?.ncFileId));
        if (!route.params?.ncFileStatus) {
          navigation.setParams({ ncFileStatus: ncFileData.searchResult?.[0]?.ncFileStatus });
        }
      }
      setNcFileIdState(ncFileData.searchResult?.[0].ncFileId);
      setNcFileDataLoading(false);
      return ncFileData.searchResult?.[0];
    }

    setNcFileDataLoading(true);
    const ncFileData = getNCFileById ? await getNCFileById(ncFileId) : null;

    if (ncFileData) {
      dispatch(setNcFileData(ncFileData));
      dispatch(setNcFileId(ncFileData?.ncFileId));
      if (!route.params?.ncFileStatus) {
        navigation.setParams({ ncFileStatus: ncFileData?.ncFileStatus });
      }
    }
    setNcFileDataLoading(false);
    return ncFileData;
  };

  const loadDataAndDefineHeader = async () => {
    if (!ncFileId && !ncFileName) return;

    const ncFileData = await loadAndSetNCFile();
    const batchesData = await loadBatches(ncFileData?.ncFileId);

    const partId = ncFileData?.partId;
    const partName = ncFileData?.partNumber;

    await loadAndSetParts(partId);

    let toolsLength = 0;

    if (getTools) {
      const currentNcFileId = ncFileId ?? ncFileData?.ncFileId;
      if (currentNcFileId) {
        ncFileIdRef.current = currentNcFileId;
      }
      const tools = await getTools({ ncFileId: currentNcFileId });
      if (tools) {
        toolsLength = tools?.length;
      }
    } else if (clearTools) {
      await clearTools();
    }

    const jobsData = await loadAndSetJobs(ncFileData?.ncFileId);

    const jobHeader =
      jobsData?.totalResultCount === 1 ? `${jobsData?.searchResult?.[0].productionOperationNumber}` : `#${jobsData?.totalResultCount ?? 0}`;

    const batchHeader = batchesData?.totalResultCount === 1 ? batchesData?.searchResult?.[0]?.batchNumber : `#${batchesData?.totalResultCount ?? 0}`;

    if (updateHeaderConfig) {
      updateHeaderConfig({
        job: jobHeader,
        batch: batchHeader,
        ncFile: route.params?.ncFileName ?? ncFileData?.ncFileName ?? '',
        ncFileId: parseRouteParamAsNumber(route.params?.ncFileId) ?? ncFileData?.ncFileId ?? '',
        ncFileStatus: ncFileData?.ncFileStatus ?? '',
        part: { id: partId ?? '', name: partName ?? '' },
        productionOrder: '',
        tools: `#${toolsLength}`,
      });
    }
  };

  useFocusEffect(
    useCallback(() => {
      let isActive = true;

      const fetchData = async () => {
        if (isActive) {
          await loadDataAndDefineHeader();
        }
      };

      fetchData();

      return () => {
        isActive = false;
      };
    }, [ncFileId, forceUpdateHeaderConfig])
  );

  const refetchTools = useCallback(() => {
    if (ncFileIdRef.current && getTools) {
      getTools({ ncFileId: ncFileIdRef.current });
    }
  }, [getTools]);

  return { ncFileDataLoading, ncFileId: ncFileId ?? ncFileIdState, partDataLoading, jobDataLoading, refetchTools };
};
